import React from 'react'
import { Checkbox } from 'vnetwork-ui'
import { useTranslation } from 'react-i18next'
import { convertCustomerGreyMarket } from '@/modules/Customer/services'
import useQueryClientCallback from '@/hooks/useQueryClientCallback'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import CheckIcons from '@/icons/CheckIcons'
import { useCustomerReadOnly } from '../../CustomerReadOnlyContext'

interface GreyMarketProps {
    customerId: number
    isGreyMarket: boolean
}

const GreyMarket: React.FC<GreyMarketProps> = ({
    customerId,
    isGreyMarket,
}) => {
    const { t } = useTranslation(['translation', 'customer'])
    const isReadOnly = useCustomerReadOnly()

    const { refetch } = useQueryClientCallback({
        mode: 'contains',
        pattern: 'customers',
    })
    const { refetch: callbackContact } = useQueryClientCallback({
        mode: 'contains',
        pattern: 'contact',
    })
    const { refetch: callbackActivity } = useQueryClientCallback({
        mode: 'contains',
        pattern: 'activities',
    })

    const { mutateAsync, isPending } = useMutationWithHandler(
        {
            mutationFn: async () => await convertCustomerGreyMarket(customerId),
        },
        {
            reactQueryKeys: QUERY_KEYS.CUSTOMERS,
            successMessage: t('updated-success'),
            errorMessage: t('Something went wrong'),
            onSuccess: () => {
                refetch()
                callbackContact()
                callbackActivity()
            },
        }
    )
    return (
        <div
            className={`p-4 rounded-md mb-4 border-[1.5px] ${
                isGreyMarket
                    ? 'bg-orange-50 border-orange-200'
                    : 'bg-gray-50 border-gray-200'
            }`}
        >
            {isGreyMarket && (
                <div className="flex items-center space-x-2 mb-2 p-2 bg-orange-100 rounded-md border border-orange-300">
                    <svg
                        className="w-4 h-4 text-orange-600 flex-shrink-0"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16c-.77.833.192 2.5 1.732 2.5z"
                        />
                    </svg>
                    <div className="flex-1">
                        <p className="text-sm font-semibold text-orange-800">
                            {t('grey-market-title')}
                        </p>
                        <p className="text-xs text-orange-700">
                            {t('grey-market-description')}
                        </p>
                    </div>
                </div>
            )}

            <div className="flex items-center space-x-2">
                <Checkbox
                    className="mt-[2px] mx-auto mb-auto"
                    name="is_market"
                    type="checkbox"
                    disabled={isPending || isReadOnly}
                    checked={isGreyMarket}
                    onChange={async () => {
                        mutateAsync()
                    }}
                />
                <div className="flex-1">
                    <label
                        className={`text-sm flex font-medium cursor-pointer capitalize ${
                            isGreyMarket ? 'text-orange-800' : 'text-gray-700'
                        }`}
                    >
                        {t('grey-market')}
                        {!isGreyMarket && (
                            <span className="ml-2 text-c-success flex items-center gap-0.5 text-xs">
                                (<CheckIcons className="w-3 h-3" />{' '}
                                {t('customer:grey-market-on')})
                            </span>
                        )}
                    </label>
                    <p className="text-xs text-gray-500 mt-1">
                        {t('grey-market-description')}
                    </p>
                </div>
            </div>
        </div>
    )
}

export default GreyMarket
